import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import UploadProgress from "../UploadProgress";
import { Variant } from "../../types/app";
import { TestBuild } from "../../types/testBuild";
import { testBuildKey } from "../../lib/testBuilds";
import postToPresignedS3 from "../../lib/s3Upload";
import testBuildService, {
  MAX_TEST_BUILD_SIZE_KB,
} from "../../features/testBuilds/services/testBuildService";
import { getApiErrorMessage } from "../../lib/api-client";

/**
 * Wider than the Apps flow's list: the test-build endpoint also accepts images,
 * so a dev can share a screenshot of the state a build reproduces alongside it.
 * Kept in sync with the backend's allowed extensions for this endpoint.
 */
const ACCEPTED_TEST_BUILD_EXTENSIONS = [
  ".apk",
  ".aab",
  ".ipa",
  ".zip",
  ".png",
  ".jpg",
  ".jpeg",
];

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot).toLowerCase() : "";
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export interface TestBuildUploadFormProps {
  projectId: string | number;
  variants: Variant[];
  variantsLoading?: boolean;
  /** Already-loaded list, used only to predict the iteration number. */
  existingBuilds: TestBuild[];
  /** Pre-fills the drop zone, e.g. from a page-level drag-and-drop capture. */
  initialFile?: File | null;
  onCancel: () => void;
  onUploaded: (iteration: number) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

const TestBuildUploadForm: React.FC<TestBuildUploadFormProps> = ({
  projectId,
  variants,
  variantsLoading = false,
  existingBuilds,
  initialFile = null,
  onCancel,
  onUploaded,
  onUploadingChange,
}) => {
  const [variantId, setVariantId] = React.useState<string>("");
  const [versionName, setVersionName] = React.useState("");
  const [versionCode, setVersionCode] = React.useState("");
  const [releaseNotes, setReleaseNotes] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  // "preparing" = fetching the presigned URL / waiting for the first byte to
  // leave the browser; "saving" = file is on S3, registering the build.
  const [phase, setPhase] = React.useState<"preparing" | "uploading" | "saving">("preparing");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const selectFile = (candidate: File) => {
    const ext = fileExtension(candidate.name);
    if (!ext) {
      setError("That file has no extension. Allowed types: APK, AAB, IPA, ZIP, PNG, JPG.");
      return;
    }
    if (!ACCEPTED_TEST_BUILD_EXTENSIONS.includes(ext)) {
      setError(`${ext} files aren't allowed. Allowed types: APK, AAB, IPA, ZIP, PNG, JPG.`);
      return;
    }
    // Checked before presigning so an oversized file fails instantly instead of
    // after a long upload.
    if (Math.ceil(candidate.size / 1024) > MAX_TEST_BUILD_SIZE_KB) {
      setError("That file is over the 512 MB limit.");
      return;
    }
    setError(null);
    setFile(candidate);
  };

  // The slide-over remounts this form for every open, so the initial file is
  // only applied once.
  React.useEffect(() => {
    if (initialFile) selectFile(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A single variant preselects silently rather than making the dev pick from
  // a list of one.
  React.useEffect(() => {
    if (variants.length === 1 && !variantId) {
      setVariantId(String(variants[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  React.useEffect(() => {
    onUploadingChange?.(uploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  // "This will be iteration #4" — the clearest signal that this pipeline stacks
  // rather than replaces. Counted from the list already on screen.
  const nextIteration = React.useMemo(() => {
    if (!variantId || !versionName.trim() || !versionCode.trim()) return null;
    const key = testBuildKey({
      variantId,
      versionName: versionName.trim(),
      versionCode: versionCode.trim(),
    });
    const matching = existingBuilds.filter((b) => testBuildKey(b) === key);
    const highest = matching.reduce((max, b) => Math.max(max, b.iteration ?? 0), 0);
    return highest + 1;
  }, [variantId, versionName, versionCode, existingBuilds]);

  const canSubmit =
    Boolean(variantId) &&
    versionName.trim() !== "" &&
    versionCode.trim() !== "" &&
    Boolean(file) &&
    !uploading;

  const handleSubmit = async () => {
    if (!file || !variantId) return;
    setUploading(true);
    setProgress(0);
    setPhase("preparing");
    setError(null);

    try {
      // Presign is only valid for 180 seconds, so it is generated here at
      // submit time and used immediately — never on dialog open.
      const { presign, serverFileName } = await testBuildService.generateUploadUrl(
        projectId,
        file
      );
      await postToPresignedS3({
        presign,
        file,
        onProgress: (percent) => {
          setProgress(percent);
          setPhase("uploading");
        },
      });
      setPhase("saving");
      const result = await testBuildService.createBuild({
        projectId,
        variantId,
        versionName: versionName.trim(),
        versionCode: versionCode.trim(),
        serverFileName,
        releaseNotes: releaseNotes.trim() || undefined,
      });
      onUploaded(result.iteration);
    } catch (err) {
      setError(
        err instanceof Error && !("response" in err)
          ? err.message
          : getApiErrorMessage(err, "Upload failed. Please try again.")
      );
      setUploading(false);
    }
  };

  const variantValue = variants.some((v) => String(v.id) === variantId)
    ? variantId
    : "";

  return (
    <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Test builds are deleted automatically after 7 days. Upload the
          finalized build through the normal Apps flow.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel id="test-build-variant-label" shrink>Variant</InputLabel>
          <Select
            labelId="test-build-variant-label"
            id="test-build-variant"
            label="Variant"
            value={variantValue}
            onChange={(e) => setVariantId(String(e.target.value))}
            disabled={uploading || variantsLoading || variants.length === 0}
            displayEmpty
            notched
          >
            <MenuItem value="" disabled>
              {variantsLoading ? "Loading variants…" : "Select a variant"}
            </MenuItem>
            {variants.map((variant) => (
              <MenuItem key={variant.id} value={String(variant.id)}>
                {variant.name}
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                  sx={{ ml: 1, fontFamily: "monospace" }}
                >
                  {variant.package_name}
                </Typography>
              </MenuItem>
            ))}
          </Select>
          {variants.length === 0 && !variantsLoading && (
            <FormHelperText error>
              This project has no variants. One must exist before a build can be
              uploaded.
            </FormHelperText>
          )}
        </FormControl>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Version Name"
            placeholder="1.4.0"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            size="small"
            fullWidth
            disabled={uploading}
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Version Code"
            placeholder="210"
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            size="small"
            fullWidth
            disabled={uploading}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <TextField
          label="Release Notes"
          placeholder="What changed in this iteration?"
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          disabled={uploading}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />

        {nextIteration !== null && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            This will be <b>iteration #{nextIteration}</b>
            {nextIteration > 1
              ? " — earlier iterations of this version stay in the list."
              : " of this version."}
          </Alert>
        )}

        <Box
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (uploading) return;
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) selectFile(dropped);
          }}
          sx={{
            borderWidth: 2,
            borderStyle: dragOver ? "solid" : "dashed",
            borderColor: dragOver ? "secondary.main" : "text.disabled",
            borderRadius: 2,
            backgroundColor: (t) =>
              dragOver ? alpha(t.palette.secondary.main, 0.05) : "transparent",
            p: 3,
            cursor: uploading ? "default" : "pointer",
            textAlign: "center",
            transition: "all 0.2s",
            "&:hover": uploading
              ? {}
              : {
                  borderColor: "secondary.main",
                  backgroundColor: (t) => alpha(t.palette.secondary.main, 0.04),
                },
          }}
        >
          <CloudUploadIcon
            sx={{ fontSize: 40, color: dragOver ? "secondary.main" : "text.disabled", mb: 1 }}
          />
          {file ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {file.name}{" "}
              <Typography component="span" variant="caption" color="textSecondary">
                ({formatSize(file.size)})
              </Typography>
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Drag &amp; drop your APK, AAB, IPA, ZIP or image here, or{" "}
              <Box
                component="span"
                sx={{ color: "secondary.main", textDecoration: "underline" }}
              >
                click to browse
              </Box>
            </Typography>
          )}
          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TEST_BUILD_EXTENSIONS.join(",")}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) selectFile(picked);
              // Reset so picking the same file twice still fires onChange.
              e.target.value = "";
            }}
          />
        </Box>

        {uploading && <UploadProgress phase={phase} progress={progress} />}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {error && file ? "Retry" : "Upload"}
        </Button>
      </Stack>
    </Box>
  );
};

export default TestBuildUploadForm;
